import { Component, type ErrorInfo, type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router-dom";
import { Button } from "@/components/ui/button.js";

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
};

/** Shown when a page or lazy chunk fails; offers a full reload to pick up a new deploy. */
export function AppErrorFallback({ onReload }: { onReload: () => void }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="max-w-md text-sm text-muted-foreground">{t("error.routeLoadFailed")}</p>
      <Button type="button" onClick={onReload}>
        {t("error.retryLoad")}
      </Button>
    </div>
  );
}

/**
 * React Router `errorElement` for failed route renders (including missing chunks after deploy).
 * Logs the route error, then shows the shared reload prompt.
 */
export function RouteErrorPage(): React.JSX.Element {
  const error = useRouteError();

  useEffect(() => {
    console.error("Route render failed", error);
  }, [error]);

  return <AppErrorFallback onReload={() => window.location.reload()} />;
}

/**
 * Catches render errors that escape the router so users still see a reload prompt
 * instead of a blank screen.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Route render failed", error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return <AppErrorFallback onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}
